const DB_NAME = "find-resume-builder-offline";
const DB_VERSION = 1;
const STORE_NAME = "saved_resume_queue";

export interface OfflineSavedResume {
  id: string;
  userId: string;
  title: string;
  content: any;
  templateId: string;
  createdAt: string;
  updatedAt: string;
  status: "queued" | "syncing" | "failed";
}

function openOfflineDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: "id" });
        store.createIndex("userId", "userId", { unique: false });
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

function runStore<T>(
  mode: IDBTransactionMode,
  operation: (store: IDBObjectStore) => IDBRequest<T> | void
): Promise<T | void> {
  return openOfflineDb().then((db) => new Promise<T | void>((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, mode);
    const store = transaction.objectStore(STORE_NAME);
    const request = operation(store);

    transaction.oncomplete = () => {
      db.close();
      resolve(request ? request.result : undefined);
    };
    transaction.onerror = () => {
      db.close();
      reject(transaction.error);
    };
  }));
}

export async function queueSavedResume(resume: Omit<OfflineSavedResume, "id" | "createdAt" | "updatedAt" | "status">) {
  const now = new Date().toISOString();
  const id = typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `offline-${Date.now()}-${Math.random().toString(16).slice(2)}`;

  const queued: OfflineSavedResume = {
    ...resume,
    id,
    createdAt: now,
    updatedAt: now,
    status: "queued",
  };

  await runStore("readwrite", (store) => store.put(queued));
  return queued;
}

export async function getQueuedSavedResumes(userId?: string) {
  const all = await runStore<OfflineSavedResume[]>("readonly", (store) => store.getAll());
  const queued = Array.isArray(all) ? all : [];
  return userId ? queued.filter((item) => item.userId === userId) : queued;
}

export async function removeQueuedSavedResume(id: string) {
  await runStore("readwrite", (store) => store.delete(id));
}

export async function updateQueuedSavedResumeStatus(id: string, status: OfflineSavedResume["status"]) {
  const item = await runStore<OfflineSavedResume>("readonly", (store) => store.get(id));
  if (!item) return;
  await runStore("readwrite", (store) => store.put({ ...item, status, updatedAt: new Date().toISOString() }));
}
